import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Plus, Trash2, Save, X, PhoneOff } from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import { demoCallersAPI } from '../services/api.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import { formatPhone } from '../lib/format.js';

// #29 — OWNER (super-admin) manager for retired-demo caller messages. When a demo is
// retired, list the numbers that may still call the old line and the custom message
// the AI speaks to each caller. Gated client-side (redirect non-owners) AND server-
// side (OWNER_EMAILS → 403). Cross-tenant / platform tool — NOT a tenant view.

const EMPTY = { phoneE164: '', label: '', message: '', scope: '' };

// Surface the server's actual validation problem (per-field) instead of a bare
// "bad_request".
function errText(err, fallback) {
  const fe = err?.body?.issues?.fieldErrors;
  if (fe) {
    const parts = Object.entries(fe)
      .map(([k, v]) => `${k}: ${(v || []).join(', ')}`)
      .filter(Boolean);
    if (parts.length) return parts.join('; ');
  }
  return err?.body?.message || err?.message || fallback;
}

// Accept common US forms and normalize to E.164.
function normPhone(raw) {
  const s = String(raw || '').trim();
  if (/^\+[1-9]\d{6,14}$/.test(s)) return s;
  const d = s.replace(/[^\d]/g, '');
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith('1')) return `+${d}`;
  return s;
}

export default function DemoCallers() {
  const { isOwner } = useAuth();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState({});
  const [creating, setCreating] = useState(EMPTY);
  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await demoCallersAPI.list();
      setItems(r?.data || []);
    } catch (err) {
      if (err.status !== 404) setError(err.message || 'Failed to load.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (isOwner) load(); }, [isOwner]);

  if (!isOwner) return <Navigate to="/" replace />;

  const startEdit = (c) =>
    setEditing((e) => ({ ...e, [c.id]: { phoneE164: c.phoneE164 || '', label: c.label || '', message: c.message || '', scope: c.scope || '' } }));
  const cancelEdit = (id) => setEditing((e) => { const n = { ...e }; delete n[id]; return n; });

  const saveEdit = async (id) => {
    const d = editing[id];
    const phoneE164 = normPhone(d.phoneE164);
    if (!/^\+[1-9]\d{6,14}$/.test(phoneE164) || !d.message.trim()) {
      setError('Enter a valid number and a message.'); return;
    }
    try {
      const r = await demoCallersAPI.update(id, { phoneE164, label: d.label.trim() || null, message: d.message.trim(), scope: d.scope.trim() || null });
      setItems((list) => list.map((i) => (i.id === id ? r.data : i)));
      cancelEdit(id);
    } catch (err) { setError(errText(err, 'Save failed.')); }
  };

  const remove = async (id, phone) => {
    if (!confirm(`Remove the message for ${formatPhone(phone)}?`)) return;
    try { await demoCallersAPI.remove(id); setItems((list) => list.filter((i) => i.id !== id)); }
    catch (err) { setError(err.message || 'Delete failed.'); }
  };

  const create = async () => {
    const phoneE164 = normPhone(creating.phoneE164);
    if (!/^\+[1-9]\d{6,14}$/.test(phoneE164) || !creating.message.trim()) {
      setError('Enter a valid number and a message.'); return;
    }
    try {
      const r = await demoCallersAPI.create({ phoneE164, label: creating.label.trim() || null, message: creating.message.trim(), scope: creating.scope.trim() || null });
      setItems((list) => [r.data, ...list.filter((i) => i.id !== r.data.id)]);
      setCreating(EMPTY);
      setAdding(false);
    } catch (err) { setError(errText(err, 'Create failed.')); }
  };

  return (
    <div className="px-6 md:px-10 py-8 max-w-3xl">
      <PageHeader
        title="Retired-demo callers"
        subtitle="When a demo is retired, callers to the old line hear the message you set here — per number."
        actions={!adding && (
          <button type="button" className="btn-primary" onClick={() => setAdding(true)}>
            <Plus size={14} /> Add number
          </button>
        )}
      />

      {error && (
        <p className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">{error}</p>
      )}

      {adding && (
        <div className="mb-6 card border border-alfred-gold/30 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <input className="field-input" placeholder="Caller number (e.g. 414-555-0100)"
              value={creating.phoneE164} onChange={(e) => setCreating({ ...creating, phoneE164: e.target.value })} autoFocus />
            <input className="field-input" placeholder="Who this is (optional, e.g. Mikheili Chantladze)"
              value={creating.label} onChange={(e) => setCreating({ ...creating, label: e.target.value })} />
          </div>
          <textarea className="field-input min-h-[90px]" placeholder="Message the AI speaks to this caller, e.g. “This demo has ended — please call 346-555-0100 to reach us.”"
            value={creating.message} onChange={(e) => setCreating({ ...creating, message: e.target.value })} />
          <input className="field-input text-sm" placeholder="Advanced: limit to one line (optional DID/demo id — blank = any line)"
            value={creating.scope} onChange={(e) => setCreating({ ...creating, scope: e.target.value })} />
          <div className="flex gap-2">
            <button type="button" className="btn-primary" onClick={create}><Save size={14} /> Save</button>
            <button type="button" className="btn-ghost" onClick={() => { setAdding(false); setCreating(EMPTY); }}><X size={14} /> Cancel</button>
          </div>
        </div>
      )}

      <section className="card">
        {loading ? (
          <p className="text-sm text-stone-500">Loading…</p>
        ) : items.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-stone-500">
            <PhoneOff size={16} /> No retired-demo caller messages yet.
          </div>
        ) : (
          <ul className="divide-y divide-stone-900">
            {items.map((c) => {
              const d = editing[c.id];
              if (d) {
                return (
                  <li key={c.id} className="py-4 space-y-3">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <input className="field-input" value={d.phoneE164}
                        onChange={(e) => setEditing((s) => ({ ...s, [c.id]: { ...s[c.id], phoneE164: e.target.value } }))} />
                      <input className="field-input" placeholder="Who this is (optional)" value={d.label}
                        onChange={(e) => setEditing((s) => ({ ...s, [c.id]: { ...s[c.id], label: e.target.value } }))} />
                    </div>
                    <textarea className="field-input min-h-[80px]" value={d.message}
                      onChange={(e) => setEditing((s) => ({ ...s, [c.id]: { ...s[c.id], message: e.target.value } }))} />
                    <input className="field-input text-sm" placeholder="Advanced: limit to one line (optional)" value={d.scope}
                      onChange={(e) => setEditing((s) => ({ ...s, [c.id]: { ...s[c.id], scope: e.target.value } }))} />
                    <div className="flex gap-2">
                      <button type="button" className="btn-primary" onClick={() => saveEdit(c.id)}><Save size={14} /> Save</button>
                      <button type="button" className="btn-ghost" onClick={() => cancelEdit(c.id)}><X size={14} /> Cancel</button>
                    </div>
                  </li>
                );
              }
              return (
                <li key={c.id} className="py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-stone-100 font-medium">{formatPhone(c.phoneE164)}</span>
                        {c.label && <span className="text-sm text-stone-400">— {c.label}</span>}
                        {c.scope && <span className="badge bg-stone-800 text-stone-300 border border-stone-700 text-[10px]">{c.scope}</span>}
                        {!c.active && <span className="badge bg-stone-800 text-stone-500 border border-stone-700 text-[10px]">inactive</span>}
                      </div>
                      <p className="mt-1 text-sm text-stone-300 whitespace-pre-wrap">{c.message}</p>
                    </div>
                    <div className="shrink-0 flex items-center gap-1">
                      <button type="button" className="btn-ghost" onClick={() => startEdit(c)}>Edit</button>
                      <button type="button" className="btn-ghost text-red-300 hover:text-red-200" onClick={() => remove(c.id, c.phoneE164)} aria-label="Delete">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
